/**
 * Reseller substrate cost & capacity constants.
 *
 * Source of truth for the Play 13 + Play 16 admin telemetry on
 * AdminServerHealthPage. These values drive:
 *   - Reseller disk-cap gauge
 *   - Bandwidth gauge
 *   - Customer-count milestone gauge (cross-referenced with whm_server.capacity_max_accounts)
 *   - Substrate-economics card
 *   - Co-lo financial model
 *   - Upgrade-path comparison
 *
 * Architecture decision (2026-05-13, see memory/competitive_analysis_substrate_economics_20260513.md):
 *
 *   We chose a TS config file (auditable, explicit, build-time visible) over
 *   a DB table because:
 *   1. Values change infrequently (quarterly at most)
 *   2. Co-lo estimates need a "pending re-quote" banner — easier to manage in code
 *   3. No customer-facing read path needs these — staff-only admin UI
 *
 *   `lastVerifiedAt` is mandatory and enforced by `assertNotStale()`.
 *   Re-verify quarterly OR on any domains.co.za pricing-page change.
 *
 *   Co-lo numbers are 2026-05-13 desk-research estimates. The
 *   `coloEstimatesVerifiedAt` field stays null until written quotes are
 *   signed from Dell SA + Teraco/Africa DC + Tier-2 IP transit + remote
 *   hands. The financial-model card renders a "pending re-quote" banner
 *   while this is null.
 */

export const RESELLER_SUBSTRATE = {
  /** ISO date when the substrate-cost figures below were last re-verified against domains.co.za pricing. */
  lastVerifiedAt: '2026-09-10',

  /** Days after which the build should warn (or fail) about stale constants. */
  staleAfterDays: 180,

  /**
   * Current reseller plan with domains.co.za.
   * Upgraded reseller_small → reseller_medium (owner, 2026-09-10). WHM
   * confirms it: `listaccts` shows afrilin1 on plan `reseller_medium` with
   * disklimit 102400M. The portal cannot read this from WHM (the token is
   * denied resellerstats/listresellers), so this block AND the two
   * system_setting rows (reseller_max_accounts, reseller_max_disk_mb) are
   * maintained by hand — change all three together.
   */
  currentPlan: {
    name: 'reseller_medium',
    diskCapMb: 102_400, // 100GB
    bwCapMb: 1_048_576, // 1TB
    accountCap: 50,
    costZarMonthly: 939,
  },

  /** Upgrade-path comparison. Same shape as currentPlan; used by Card E. */
  upgradePaths: [
    { name: 'reseller_small', label: 'Small', diskCapMb: 51_200, bwCapMb: 1_048_576, accountCap: 25, costZarMonthly: 419, isCurrent: false },
    { name: 'reseller_medium', label: 'Medium (us)', diskCapMb: 102_400, bwCapMb: 1_048_576, accountCap: 50, costZarMonthly: 939, isCurrent: true },
    { name: 'reseller_large', label: 'Large', diskCapMb: 204_800, bwCapMb: 1_048_576, accountCap: 100, costZarMonthly: 1_769, isCurrent: false },
    { name: 'reseller_xl', label: 'XL', diskCapMb: 409_600, bwCapMb: 1_048_576, accountCap: 200, costZarMonthly: 3_399, isCurrent: false },
    { name: 'colo', label: 'Co-locate', diskCapMb: 2_000_000, bwCapMb: 10_000_000, accountCap: 500, costZarMonthly: 2_600, isCurrent: false, isCoLo: true },
  ],

  /**
   * Decision-trigger thresholds (per addendum §5 + locked memory), re-based
   * on 2026-09-10 for the 50-account Medium plan using the same proportions
   * as the original 25-account ladder (70% / 90% / 100%). The co-locate
   * trigger stays where the 2026-05-13 economics put it: decide at 40, and
   * the Large plan (100 accounts, R1,769) is the reseller scale-up in between.
   */
  milestones: {
    comfortableMax: 35,
    planAheadMax: 45,
    decideNowMax: 50,
    resellerScaleUpMax: 89,
    // 90+ = co-locate
  },

  /** Alarm thresholds for the disk + bandwidth gauges. */
  alarms: {
    /** % of reseller cap at which we warn (amber). */
    diskWarnPct: 60,
    /** % of reseller cap at which we alarm (red). */
    diskCriticalPct: 80,
    /** Any single account using this many MB triggers a per-account alarm. */
    singleAccountWarnMb: 20_480, // 20GB
    /** Bandwidth thresholds (same shape). */
    bwWarnPct: 60,
    bwCriticalPct: 80,
  },

  /**
   * Co-lo financial-model estimates (desk research 2026-05-13).
   *
   * **DO NOT USE FOR ACTUAL DECISIONS UNTIL `coloEstimatesVerifiedAt` IS SET.**
   *
   * Required re-quotes before going live:
   * - Server capex: Dell SA / Pinnacle / First Distribution on current-gen R650 spec
   * - Co-lo opex: Teraco AND Africa DC (CT + JHB) — 1U + power + bandwidth
   * - IP transit: Vox / Liquid / Internet Solutions
   * - Remote hands: chosen co-lo provider
   */
  coloEstimates: {
    /** ISO date the four supplier quotes were last signed. Null = pending. */
    verifiedAt: null as string | null,
    capex: {
      serverZar: 150_000,
      sparesSetupZar: 10_000,
      totalZar: 160_000,
    },
    opex: {
      coloRackZar: 1_500, // 1U + power
      ipTransitZar: 800, // 1Gbps
      remoteHandsAvgZar: 300,
      totalZarMonthly: 2_600,
    },
  },

  /**
   * Per-customer substrate cost benchmarks for the comparison card.
   * Sourced from the addendum's cost analysis. Approximate, not contractual.
   */
  competitorBenchmark: {
    afrihostPerCustomerZar: 8,
    xneeloPerCustomerZar: 18,
  },
} as const;

/**
 * Compute days since `lastVerifiedAt`. Used by build-time staleness check.
 */
export function daysSinceVerified(): number {
  const verified = new Date(RESELLER_SUBSTRATE.lastVerifiedAt).getTime();
  return Math.floor((Date.now() - verified) / (1000 * 60 * 60 * 24));
}

/**
 * Throw if the constants haven't been re-verified within `staleAfterDays`.
 * Call from a build-time check OR from the admin page on first render to
 * surface a banner.
 */
export function assertNotStale(): void {
  const days = daysSinceVerified();
  if (days > RESELLER_SUBSTRATE.staleAfterDays) {
    throw new Error(
      `resellerSubstrate.ts constants stale (${days} days since lastVerifiedAt=${RESELLER_SUBSTRATE.lastVerifiedAt}). ` +
      'Re-verify against domains.co.za pricing and bump lastVerifiedAt.',
    );
  }
}

/**
 * Soft check — returns staleness status without throwing. UI cards call this
 * to render a yellow "values may be outdated" banner.
 */
export function getStalenessStatus(): { isStale: boolean; daysSinceVerified: number; verifiedAt: string } {
  const days = daysSinceVerified();
  return {
    isStale: days > RESELLER_SUBSTRATE.staleAfterDays,
    daysSinceVerified: days,
    verifiedAt: RESELLER_SUBSTRATE.lastVerifiedAt,
  };
}

/** Current bracket given a live customer count. Used by Card B + status pill. */
export function customerMilestoneBracket(count: number): 'comfortable' | 'plan_ahead' | 'decide_now' | 'reseller_scale_up' | 'co_locate' {
  const m = RESELLER_SUBSTRATE.milestones;
  if (count <= m.comfortableMax) return 'comfortable';
  if (count <= m.planAheadMax) return 'plan_ahead';
  if (count <= m.decideNowMax) return 'decide_now';
  if (count <= m.resellerScaleUpMax) return 'reseller_scale_up';
  return 'co_locate';
}

/** Substrate cost per customer at a given customer count. R∞ when count is 0. */
export function costPerCustomerZar(customerCount: number): number {
  if (customerCount <= 0) return Infinity;
  return RESELLER_SUBSTRATE.currentPlan.costZarMonthly / customerCount;
}
